import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import apiFetch from "../../util/fetch-utils";
import { GoogleAccountSelect, tokenOption } from "../GoogleAccountSelect";
import { SettingsDialog, Field, FieldLabel, ErrorMsg, WarningNote } from "../SettingsDialog";
import { portalledMenu } from "../portalledMenu";
import { cappedMenuList } from "../cappedMenuList";
import { filterByNameOrId } from "../optionFilter";
import { groupSiteOptions, siteOption } from "./siteOptions";
import { groupRow, siteRow } from "./siteRow";

export const SearchConsoleSettingsModal = (props) => {
    const settings = props.searchConsoleSettings || {};

    const [token, setToken] = useState(null);
    const [site, setSite] = useState(null);
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingFailure, setLoadingFailure] = useState(null);

    const tokenName = token?.value;
    // An account can reach hundreds of properties, and grouping them sorts the lot: without this it
    // would run again on every keystroke typed into the property filter.
    const siteOptions = useMemo(() => groupSiteOptions(sites), [sites]);

    // Reopening the dialog starts from what is saved, so cancelling a half-made change leaves
    // nothing behind.
    useEffect(() => {
        if (!props.isOpen) return;
        const saved = props.analyticsTokens.find(t => t.name === settings.tokenName);
        setToken(saved ? tokenOption(saved) : null);
        setSite(settings.siteUrl ? siteOption(settings.siteUrl) : null);
    }, [props.isOpen]);

    useEffect(() => {
        if (!tokenName) return setSites([]);
        setLoading(true);
        setLoadingFailure(null);
        apiFetch(`/api/google/search-console/sites?tokenName=${tokenName}`)
            .then(res => res.json().catch(() => ({})).then(body => {
                if (res.status !== 200) return setLoadingFailure(failureReading(body.reason));
                setSites(body.map(property => property.siteUrl));
            }))
            .catch(() => setLoadingFailure(failureReading()))
            .finally(() => setLoading(false));
    }, [tokenName]);

    return (
        <SettingsDialog
            title="Search Console connection"
            workspace={props.workspace}
            isOpen={props.isOpen}
            onClose={props.toggle}
            onSave={onSave}
            saveDisabled={!site?.value || loadingFailure !== null}
        >
            <Field>
                <FieldLabel>Google account</FieldLabel>
                <GoogleAccountSelect
                    value={token}
                    onChange={option => { setToken(option); setSite(null); setLoadingFailure(null); }}
                    isClearable
                />
                {loadingFailure?.kind === "note" && <WarningNote>{loadingFailure.message}</WarningNote>}
                {loadingFailure?.kind === "error" && <ErrorMsg>{loadingFailure.message}</ErrorMsg>}
            </Field>

            <Field>
                <FieldLabel>Property</FieldLabel>
                <Select
                    {...portalledMenu}
                    isClearable
                    isLoading={loading}
                    isDisabled={!tokenName || loadingFailure !== null}
                    value={site}
                    onChange={setSite}
                    options={siteOptions}
                    formatOptionLabel={siteRow}
                    formatGroupLabel={groupRow}
                    components={{ MenuList: cappedMenuList }}
                    filterOption={filterByNameOrId}
                    placeholder="Select a property…"
                />
            </Field>
        </SettingsDialog>
    );

    // The backend answers 403 and a reason whenever the connection is there but cannot serve the
    // call. Both reasons are the user's to put right, so they read as a note rather than a failure.
    function failureReading(reason) {
        if (reason === "SCOPE_NOT_GRANTED") return {
            kind: "note",
            message: "Search Console access has not been granted for this Google account. Reconnect it with the + button and allow Search Console when Google asks, or pick a different account.",
        };
        if (reason === "AUTHORIZATION_REJECTED") return {
            kind: "note",
            message: "Google no longer accepts this connection, so it has to be granted again. Reconnect the account with the + button, or pick a different account.",
        };
        return { kind: "error", message: "Unable to read this Search Console connection. Pick another account, or connect one with +." };
    }

    function onSave() {
        props.setSearchConsoleSettings({ tokenName, siteUrl: site.value });
        props.toggle();
    }
};
